import { ShieldCheck, CheckCircle2, AlertCircle, AlertTriangle, ExternalLink, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const LEVEL_CLS = {
  critical: 'border-destructive/30 bg-destructive/10',
  warning:  'border-warning/30 bg-warning/10',
  info:     'border-info/30 bg-info/10',
  ok:       'border-success/30 bg-success/10',
};

const ICONS = {
  critical: AlertCircle,
  warning:  AlertTriangle,
  info:     ShieldCheck,
  ok:       CheckCircle2,
};

const ICON_CLS = {
  critical: 'text-destructive',
  warning:  'text-warning',
  info:     'text-info',
  ok:       'text-success',
};

/**
 * VerifactuValidationResult — shown after validar_factura_vs_verifactu.
 * Read-only validation card. If alert_level=ok, compact green badge.
 * Otherwise shows summary + cited article + recommendation.
 */
export default function VerifactuValidationResult({ result }) {
  if (!result) return null;

  const level = result.alert_level ?? 'info';
  const Icon = ICONS[level] ?? ShieldCheck;
  const cls  = LEVEL_CLS[level] ?? LEVEL_CLS.info;
  const iconCls = ICON_CLS[level] ?? ICON_CLS.info;

  // OK state — compact
  if (level === 'ok') {
    return (
      <div className={`rounded-xl border ${cls} px-3 py-2.5 flex items-center gap-2 my-1 max-w-sm`}>
        <Icon className={`w-4 h-4 ${iconCls} shrink-0`} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground truncate">
            Factura {result.invoice_number ?? ''} conforme a Verifactu
          </p>
          <p className="text-[10px] text-muted-foreground">
            IVA {result.iva_aplicado_pct?.toFixed(1)}%
            {result.irpf_aplicado_pct > 0 && ` · IRPF ${result.irpf_aplicado_pct.toFixed(1)}%`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border ${cls} p-3 my-1 max-w-sm space-y-2`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-4 h-4 ${iconCls} shrink-0`} />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground">
              Verifactu · {result.invoice_number ?? 'Factura'}
            </p>
            <p className="text-[10px] text-muted-foreground">
              IVA {result.iva_aplicado_pct?.toFixed(1)}%
              {result.irpf_aplicado_pct > 0 && ` · IRPF ${result.irpf_aplicado_pct.toFixed(1)}%`}
            </p>
          </div>
        </div>
        {result.invoiceId && (
          <Link to={`/facturas?invoice=${result.invoiceId}`}>
            <Button size="sm" variant="outline" className="h-7 text-[11px] shrink-0 gap-1">
              Ver
              <ExternalLink className="w-3 h-3" />
            </Button>
          </Link>
        )}
      </div>

      <p className="text-xs text-foreground leading-snug">{result.summary}</p>

      {result.article_reference && (
        <div className="rounded-md sf-surface p-2 border border-indigo-200 dark:border-indigo-800/60">
          <div className="flex items-center gap-1 mb-1">
            <Sparkles className="w-3 h-3 text-indigo-500" />
            <span className="text-[10px] uppercase tracking-wider font-medium text-indigo-700 dark:text-indigo-300">
              Normativa
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug">{result.article_reference}</p>
        </div>
      )}

      {result.recommendation && (
        <p className="text-[11px] text-muted-foreground italic border-t border-border pt-2">
          → {result.recommendation}
        </p>
      )}

      {Array.isArray(result.anomalies) && result.anomalies.length > 0 && (
        <details className="text-[10px] text-muted-foreground">
          <summary className="cursor-pointer">Ver {result.anomalies.length} anomalía{result.anomalies.length !== 1 ? 's' : ''}</summary>
          <ul className="mt-1 ml-3 list-disc space-y-0.5">
            {result.anomalies.map((a, i) => (
              <li key={i} className="leading-snug">{a}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
